/**
 * Distinguer une alerte VUE d'une alerte LUE (2026-09-28).
 *
 * <p>Le badge de la cloche comptait les alertes non lues, et une alerte n'était lue qu'une fois
 * cliquée. Ouvrir la cloche pour regarder la liste ne changeait donc rien : le badge restait
 * affiché, et l'opérateur le croyait figé.
 *
 * <p>Deux notions désormais :
 * <ul>
 *   <li><b>vue</b> — présente dans la liste au moment où la cloche a été ouverte. Le badge ne
 *       compte plus que les alertes arrivées APRÈS la dernière ouverture : il signale du
 *       nouveau, rien d'autre ;</li>
 *   <li><b>lue</b> — cliquée. Inchangé, et toujours enregistré par le serveur. Une alerte vue
 *       mais pas cliquée reste en gras dans la liste : ce qui n'a pas été traité se voit encore.</li>
 * </ul>
 *
 * <p>Le repère « vu jusqu'à » est gardé dans le navigateur, par utilisateur. Le perdre (autre
 * poste, données effacées) fait seulement réapparaître le badge jusqu'à la prochaine ouverture :
 * aucune alerte n'est jamais masquée.
 */

interface AlerteReperable {
    id: number;
    lu: boolean;
}

const PREFIXE = 'alertes.dernierVu.';

export const cleDernierVu = (utilisateur: string | undefined): string =>
    PREFIXE + (utilisateur || 'anonyme');

/** Zéro si rien n'est enregistré ou si le stockage est inaccessible (navigation privée…). */
export const lireDernierVu = (cle: string): number => {
    try {
        const brut = window.localStorage.getItem(cle);
        const n = brut === null ? 0 : Number(brut);
        return Number.isFinite(n) ? n : 0;
    } catch {
        return 0;
    }
};

export const ecrireDernierVu = (cle: string, id: number): void => {
    try {
        window.localStorage.setItem(cle, String(id));
    } catch {
        // Stockage indisponible : le badge réapparaîtra au prochain chargement, sans plus.
    }
};

/** Le plus grand identifiant présent — ce que « tout ce qui est affiché » recouvre. */
export const plusGrandId = (alertes: AlerteReperable[]): number =>
    alertes.reduce((max, a) => (a.id > max ? a.id : max), 0);

/**
 * Ce que le badge affiche : les alertes ni lues, ni vues. Une alerte cliquée ne compte plus
 * même si elle est plus récente que le repère.
 */
export const compterNouvelles = (alertes: AlerteReperable[], dernierVu: number): number =>
    alertes.filter(a => !a.lu && a.id > dernierVu).length;
